import { CloseIcon } from "@chakra-ui/icons";
import {
  AspectRatio,
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  Icon,
  Image,
} from "@chakra-ui/react";
import Loader from "@components/Loader/LoadingAnimation";
import { to } from "@lib/utils";
import { DEFAULT_LANGUAGE, Language, translate as t } from "@models/language";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  FieldErrors,
  FieldValues,
  RegisterOptions,
  UseFormRegister,
} from "react-hook-form";
import DynamicFormFieldHeader from "./DynamicFormFieldHeader";
import { restrictedUploadImage } from "./services/upload";
import { Field, ImageUploadFormField } from "./types";

const MAX_FILE_SIZE = 5; // MB

// TODO: default translation
const _t = (key?: string, language = DEFAULT_LANGUAGE) => key || "";

const DynamicFormUpload = ({
  field,
  errors,
  register,
  language: language = DEFAULT_LANGUAGE,
  isDirty: isDirty = true,
  className: className = "",
}: {
  field: ImageUploadFormField;
  errors: FieldErrors<FieldValues>;
  register: UseFormRegister<FieldValues>;
  language?: Language;
  accept?: string;
  isDirty?: boolean;
  className?: string;
}) => {
  const [previewURL, setPreviewURL] = useState<string | null>(null);
  const [uploading, setUploading] = useState<boolean>(false);
  const [uploadError, setUploadError] = useState<boolean>(false);
  const [uploadErrorMessage, setUploadErrorMessage] = useState<string | null>(
    null
  );
  const inputRef = useRef<HTMLInputElement | null>(null);

  // set previewURL if default value is provided
  useEffect(() => {
    if (field.defaultImage) {
      setPreviewURL(field.defaultImage);
      if (field.onSuccess) field.onSuccess(field.defaultImage);
    }
  }, [field.defaultImage]);

  const handleFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFile = event.target.files && event.target.files[0];
      console.log({ selectedFile });
      if (!selectedFile) return;
      if (!selectedFile.type.includes("image")) {
        setUploadError(true);
        setUploadErrorMessage("File type not supported, please try again");
        return;
      }

      const maxSize = field.maxSize || MAX_FILE_SIZE; // 5MB
      if (selectedFile.size > maxSize * 1024 * 1024) {
        setUploadError(true);
        setUploadErrorMessage(`File size too large, maximum is ${maxSize} MB`);
        return;
      }

      const reader = new FileReader();
      reader.readAsDataURL(selectedFile);

      // previews image before upload
      //const previewURL = await new Promise<string>((resolve) => {
      //  reader.onload = () => resolve(reader.result as string);
      //});
      //setPreviewURL(previewURL);

      setUploading(true);
      if (field.onStartUploading) field.onStartUploading();
      setPreviewURL(null);
      setUploadError(false);
      setUploadErrorMessage(null);

      const { err, res: url } = await to(
        restrictedUploadImage(selectedFile, field.uploadParams)
      );
      if (!url) {
        setUploading(false);
        if (field.onEndUploading) field.onEndUploading();
        console.error(err);
        // TODO: handle error
        setUploadError(true);
        setUploadErrorMessage("Upload failed, please try again");
        inputRef.current?.value && (inputRef.current.value = "");
        return;
      }
      setUploading(false);
      if (field.onEndUploading) field.onEndUploading();
      if (field.onSuccess) field.onSuccess(url);
      setPreviewURL(url);
    },
    [field.uploadParams, field.onSuccess]
  );

  // https://tailwindcss.com/docs/content-configuration#dynamic-class-names
  let colSpan = "sm:col-span-1";
  switch (field.span) {
    case 1:
      colSpan = "sm:col-span-1";
      break;
    case 2:
      colSpan = "sm:col-span-2";
      break;
    case 3:
      colSpan = "sm:col-span-3";
      break;
    case 4:
      colSpan = "sm:col-span-4";
      break;
  }

  const toRegisterOptions = (
    field: Field
  ): RegisterOptions<FieldValues, string> => {
    return {
      ...(field.required
        ? {
            required:
              t(field.errorMessages?.required, language) ||
              _t("This field is required", language),
          }
        : {}),
    };
  };

  const handleClick = () => inputRef.current?.click();

  const { ref, ...rest } = register(field.key, {
    ...toRegisterOptions(field),
    onChange: handleFileChange,
  }) as { ref: (instance: HTMLInputElement | null) => void };

  const previewRatio = useMemo(
    () => (field.dimension?.width || 1) / (field.dimension?.height || 1),
    [field.dimension]
  );

  const handleDelete = () => {
    setPreviewURL(null);
    inputRef.current?.value && (inputRef.current.value = "");
    if (field.onDelete) {
      field.onDelete();
    }
  };

  return (
    <>
      <FormControl
        isInvalid={isDirty && !!errors[field.key]}
        className={`${field.span ? colSpan : ""} col-span-1 ${className}`}
        isRequired={field.required || field.confirm}
        isDisabled={field.disabled}
        isReadOnly={field.readonly}
      >
        <div
          onMouseOver={() => {
            if (field?.updateCurrentEventPreviewPart) {
              field?.updateCurrentEventPreviewPart(field.key);
            }
          }}
          onMouseLeave={() => {
            if (field?.updateCurrentEventPreviewPart) {
              field?.updateCurrentEventPreviewPart("DUMMY");
            }
          }}
        >
          <DynamicFormFieldHeader field={field} language={language} />
          <Box
            className="flex flex-col justify-center items-center p-8 gap-3 bg-opacity-50 border-dashed border-2 rounded border"
            w={`${field.container?.width ?? 50}%`}
          >
            <input
              id={field.key}
              hidden
              accept={field.accept}
              type={"file"}
              placeholder={t(field.placeholder, language)}
              {...rest}
              ref={(e) => {
                ref(e);
                inputRef.current = e;
              }}
            />
            <div className="flex flex-col justify-center items-center w-full h-full">
              {previewURL && !uploading && (
                <div className="relative max-w-full">
                  <AspectRatio
                    w={Math.max(field.dimension?.width || 300)}
                    maxW="full"
                    ratio={previewRatio}
                    position="relative"
                  >
                    <Image
                      objectFit="cover"
                      src={previewURL}
                      alt="Preview"
                      className="rounded-md"
                      border="1px"
                      borderColor="neutral.60"
                    />
                  </AspectRatio>

                  {!(field.disabled || field.readonly) && (
                    <div
                      className="absolute inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white z-10 bg-error rounded-full -top-2 -right-2 cursor-pointer"
                      onClick={handleDelete}
                    >
                      <Icon as={CloseIcon} boxSize={2} />
                    </div>
                  )}
                </div>
              )}
              {!previewURL && !uploading && (
                <AspectRatio
                  w={Math.max(field.dimension?.width || 300)}
                  maxW="full"
                  ratio={previewRatio}
                >
                  <p className="flex flex-row justify-center items-center bg-white dark:bg-neutral-850 rounded-md text-primary-200 font-medium text-center">
                    {field.dimension &&
                      `${field.dimension.width} x ${field.dimension.height}`}
                    {field.dimension && <br />}
                    {`Max. size: ${field.maxSize || 5} MB`}
                  </p>
                </AspectRatio>
              )}
            </div>
            {uploading && (
              <AspectRatio
                w={Math.max(field.dimension?.width || 300)}
                maxW="full"
                ratio={previewRatio}
              >
                <Loader />
              </AspectRatio>
            )}
            {!(field.disabled || field.readonly || uploading) && (
              <Button
                variant="gradient"
                className="w-fit"
                onClick={handleClick}
                isDisabled={field.disabled || field.readonly}
                size="sm"
              >
                Upload
              </Button>
            )}
            {uploadError && <p className="text-error">{uploadErrorMessage} </p>}
          </Box>
        </div>
        <FormErrorMessage>
          {(!previewURL &&
            errors[field.key] &&
            String(errors[field.key]?.message)) ||
            ""}
        </FormErrorMessage>
      </FormControl>
    </>
  );
};

export default DynamicFormUpload;
